import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  AdhesionTypeStep,
  ConfirmationStep,
  MembershipStep,
  MembershipSubscriptionStep,
  OtherUserInfoStep,
  PostalUserInfoStep,
  StatusStep,
  UserInfoWithoutMembershipStep
} from 'components';
import { appService } from 'services';
import { addStep } from 'store';

const RegistrationForm = () => {
  const stepComponents = {
    statusStep: StatusStep,
    membershipStep: MembershipStep,
    adhesionTypeStep: AdhesionTypeStep,
    userInfoWithoutMembershipStep: UserInfoWithoutMembershipStep,
    postalUserInfoStep: PostalUserInfoStep,
    otherUserInfoStep: OtherUserInfoStep,
    membershipSubscriptionStep: MembershipSubscriptionStep,
    confirmationStep: ConfirmationStep
  };

  const { t } = useTranslation();
  const isTouloisirs = appService.isTouloisirs();

  const { onSubmit, getValues } = useFormContext();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const steps = useSelector((state) => state.auth.steps);
  const currentStep = [...steps].pop();
  const StepComponent = stepComponents[currentStep];
  const isConfirmationOther = searchParams.get('step') === 'confirmation-other';

  const stepTitle = useMemo(() => {
    switch (currentStep) {
      case 'adhesionTypeStep':
      case 'membershipSubscriptionStep':
        return 'yourAdhesion';
      case 'confirmationStep':
        if (isConfirmationOther) {
          return 'welcomeAdhesion';
        } else {
          return isTouloisirs ? 'welcome' : 'welcomeArrivaj';
        }
      default:
        return 'createAccount';
    }
  }, [currentStep]);

  useEffect(() => {
    if (searchParams.get('step')) {
      dispatch(addStep('confirmationStep'));
    }
  }, [searchParams]);

  const changeStep = () => {
    // Redirect from step 1 (status) to step 2 (membershipFee)
    if (currentStep === 'statusStep' && getValues('employementStatus') === 'postalPensionerOrOrangeworker') {
      dispatch(addStep('membershipStep'));
    }
    // Redirect from step 1 (status) to step 4 (postalUserInfo)
    if (currentStep === 'statusStep' && getValues('employementStatus') === 'activePostalWorker') {
      dispatch(addStep('postalUserInfoStep'));
    }
    // Redirect from step 1 (status) to step 4bis (otherUserInfo)
    if (currentStep === 'statusStep' && getValues('employementStatus') === 'otherEmployee') {
      dispatch(addStep('otherUserInfoStep'));
    }
    // Redirect from step 2 (membershipFee) to step 3 (adhesionType)
    if (currentStep === 'membershipStep' && getValues('membershipFee') === 'notMemberYet') {
      dispatch(addStep('adhesionTypeStep'));
    }
    // Redirect from step 2 (membershipFee) to step 4ter (UserWithoutMembership)
    if (currentStep === 'membershipStep' && getValues('membershipFee') === 'alreadyPaidSubscription') {
      dispatch(addStep('userInfoWithoutMembershipStep'));
    }
    // Redirect from step 3 (adhesionType) to step 4ter (UserWithoutMembership)
    if (currentStep === 'adhesionTypeStep') {
      dispatch(addStep('userInfoWithoutMembershipStep'));
    }
    // Redirect from step 4ter (UserWithoutMembership) to step 5 (MembershipSubscription)
    if (currentStep === 'userInfoWithoutMembershipStep') {
      dispatch(addStep('membershipSubscriptionStep'));
    }
    // Redirect from step 5 (MembershipSubscription) to step 6 (Confirmation with registration PDF)
    if (currentStep === 'membershipSubscriptionStep') {
      dispatch(addStep('confirmationStep'));
      setSearchParams({ ...searchParams, step: 'confirmation-other' });
    }
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    onSubmit(currentStep);
  };

  return (
    <>
      <h1 className="h1 underlined-yellow mb-6">{t(`auth:registration.steps.${stepTitle}`)}</h1>

      <form onSubmit={handleSubmit}>
        <StepComponent changeStep={changeStep} />
      </form>
    </>
  );
};

export default RegistrationForm;
